import React, { useState } from 'react';
import { auth } from '../firebase';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification
} from 'firebase/auth';
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  useToast,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  FormErrorMessage
} from '@chakra-ui/react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { getFirebaseErrorMessage } from '../utils/firebaseErrors';

export default function SignUpScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();

  // バリデーション
  const isEmailValid = email.includes('@') && email.includes('.');
  const isPasswordValid = password.length >= 6;
  const isConfirmPasswordValid = password === confirmPassword && confirmPassword.length > 0;
  const isFormValid = isEmailValid && isPasswordValid && isConfirmPasswordValid;

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    
    if (!isFormValid) {
      toast({
        title: "入力エラー",
        description: "入力内容を確認してください。",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(userCredential.user);
      setEmailSent(true);
      toast({
        title: "アカウント作成完了",
        description: "確認メールを送信しました。メールを確認してください。",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Email SignUp Error:", error);
      const message = getFirebaseErrorMessage(error);
      toast({
        title: "サインアップエラー",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <Box minH="100vh" bg="gray.50" py={10} display="flex" alignItems="center" justifyContent="center">
        <Container maxW="md">
          <Box bg="white" p={8} borderRadius="lg" boxShadow="lg">
            <VStack spacing={6}>
              <Alert status="success">
                <AlertIcon />
                <Box>
                  <AlertTitle>アカウント作成完了！</AlertTitle>
                  <AlertDescription>
                    {email} に確認メールを送信しました。
                    <br />
                    メール内のリンクをクリックして、メールアドレスの確認を完了してください。
                  </AlertDescription>
                </Box>
              </Alert>
              
              <Button
                as={RouterLink}
                to="/auth"
                bg="black"
                color="white"
                w="full"
              >
                ログイン画面に戻る
              </Button>
            </VStack>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" py={10} display="flex" alignItems="center" justifyContent="center">
      <Container maxW="md">
        <Box bg="white" p={8} borderRadius="lg" boxShadow="lg">
          <VStack spacing={6}>
            <Heading size="lg" color="gray.700">新規アカウント作成</Heading>
            <Text color="gray.600">メールアドレスとパスワードを入力してください。</Text>

            <form onSubmit={handleEmailSignUp} style={{ width: '100%' }}>
              <VStack spacing={4} w="100%">
                <FormControl isRequired isInvalid={email.length > 0 && !isEmailValid}>
                  <FormLabel>メールアドレス</FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@example.com"
                    autoComplete="email"
                  />
                  {email.length > 0 && !isEmailValid && (
                    <FormErrorMessage>有効なメールアドレスを入力してください</FormErrorMessage>
                  )}
                </FormControl>

                <FormControl isRequired isInvalid={password.length > 0 && !isPasswordValid}>
                  <FormLabel>パスワード</FormLabel>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="6文字以上"
                    autoComplete="new-password"
                  />
                  {password.length > 0 && !isPasswordValid && (
                    <FormErrorMessage>パスワードは6文字以上で入力してください</FormErrorMessage>
                  )}
                </FormControl>

                <FormControl isRequired isInvalid={confirmPassword.length > 0 && !isConfirmPasswordValid}>
                  <FormLabel>パスワード確認</FormLabel>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="パスワードを再入力"
                    autoComplete="new-password"
                  />
                  {confirmPassword.length > 0 && !isConfirmPasswordValid && (
                    <FormErrorMessage>パスワードが一致しません</FormErrorMessage>
                  )}
                </FormControl>

                <Button
                  type="submit"
                  bg="black"
                  color="white"
                  size="lg"
                  w="full"
                  isLoading={isLoading}
                  loadingText="作成中..."
                  isDisabled={!isFormValid}
                >
                  アカウントを作成
                </Button>

                <Button
                  as={RouterLink}
                  to="/auth"
                  variant="link"
                  colorScheme="gray"
                >
                  ログイン画面に戻る
                </Button>
              </VStack>
            </form>
          </VStack>
        </Box>
      </Container>
    </Box>
  );
} 